import moment, { Moment } from "moment"
import { chain, get, isString, isEmpty } from "lodash"
import { IsoDate } from "./types/dates"

const PERIOD_MATCH = /(\d+)(\D+)/

export type Period = "1D" | "1M" | "3M" | "6M" | "MTD" | "YTD" | "1Y" | "3Y" | "CUSTOM" | "SAME_DAY"
export interface PeriodObject {
  startDate: IsoDate
  endDate: IsoDate
}

export const PERIOD_OPTIONS: { value: Period, label: string }[] = [
  { value: "1D", label: "1 Day" },
  { value: "MTD", label: "MTD" },
  { value: "1M", label: "1 Month" },
  { value: "3M", label: "3 Months" },
  { value: "6M", label: "6 Months" },
  { value: "YTD", label: "YTD" },
  { value: "1Y", label: "1 Year" },
  { value: "3Y", label: "3 Years" },
  { value: "CUSTOM", label: "Select Custom Range..." },
]

export const DATE_FORMAT = "YYYY-MM-DD"
export const YEAR_MONTH_FORMAT = "YYYY-MM"
export const TIME_FORMAT_TZ = "HH:mm:ssZ"
export const DATE_FORMAT_LONG = "MMMM D, YYYY"

export const MONTH_TO_DATE = "MTD"
export const YEAR_TO_DATE = "YTD"
export const ONE_DAY = "1D"
export const ONE_MONTH = "1M"
export const THREE_MONTHS = "3M"
export const SIX_MONTHS = "6M"
export const ONE_YEAR = "1Y"
export const THREE_YEARS = "3Y"
export const SAME_DAY = "SAME_DAY"
export const CUSTOM_PERIOD = "CUSTOM"

function momentToIsoDate(date: Moment) {
  return date.format(DATE_FORMAT) as IsoDate
}

function periodToDate(endDate: IsoDate, period: Period) {
  const parts = PERIOD_MATCH.exec(period)

  if (parts?.length === 3) {
    const startDate = moment(endDate).subtract(parts[1], parts[2] as moment.unitOfTime.DurationConstructor)

    if (startDate.isValid()) {
      const isoStartDate = momentToIsoDate(startDate)

      if (isoStartDate !== endDate) {
        return isoStartDate
      }
    }
  }

  return null
}

export function stringToIsoDate(date: string) {
  if (!moment(date, DATE_FORMAT, true).isValid()) {
    throw Error(`Invalid ISO Date: ${date}`)
  }

  return date as IsoDate
}

// Extracts YYYY-MM-DD from an ISO date or datetime string (e.g. "2026-04-28T14:30:00Z" → "2026-04-28").
export function toIsoDate(date: string | null | undefined): IsoDate | null {
  return date ? (date.slice(0, 10) as IsoDate) : null
}

export function isUnmodeledDate(date: IsoDate, unmodeledDates?: IsoDate[] | Record<IsoDate, boolean> | null) {
  // Check if it's a weekend
  if (moment(date).isoWeekday() > 5) {
    return true
  }

  if (!unmodeledDates) {
    return false
  }

  // Handle array format: ["2017-02-01", "2017-02-02"]
  if (Array.isArray(unmodeledDates)) {
    return unmodeledDates.includes(date)
  }

  // Handle object format: { "2017-02-01": true, "2017-02-02": true }
  return Boolean(get(unmodeledDates, date))
}

export function getToday() {
  return momentToIsoDate(moment())
}

export function formatDateToString(date: IsoDate | Moment, format = DATE_FORMAT) {
  if (moment.isMoment(date)) {
    return date?.format(format)
  }
  return date
}

export const computeStartDate = (earliestDate: IsoDate, startDate: IsoDate) =>
  earliestDate > startDate
    ? earliestDate
    : startDate

export const computeLatestDate = (assetEndDate: IsoDate, modelEndDate: IsoDate) =>
  assetEndDate <= modelEndDate
    ? assetEndDate
    : modelEndDate

export function getStartDate(endDate: IsoDate, period: Period) {
  const end = moment(endDate)

  switch (period) {
    case MONTH_TO_DATE:
      return end.startOf("month").format(DATE_FORMAT)
    case YEAR_TO_DATE:
      return end.startOf("year").format(DATE_FORMAT)
    case ONE_DAY:
    case SAME_DAY:
      return end.format(DATE_FORMAT)
  }

  return periodToDate(endDate, period)
}

function ensureValidStartDate(computedStartDate: IsoDate, earliestDate: IsoDate, latestDate: IsoDate, unmodeledDates: IsoDate[] | null) {
  const boundedStartDate = computeStartDate(earliestDate, computedStartDate)

  if (isUnmodeledDate(boundedStartDate, unmodeledDates)) {
    return getNextModeledDate(boundedStartDate, latestDate, unmodeledDates)
  }

  return boundedStartDate
}

export function getStartDateWithBoundaries(endDate: IsoDate, period: Period | PeriodObject, earliestDate: IsoDate, latestDate: IsoDate, unmodeledDates: IsoDate[] | null, isPerformance: boolean) {
  if (!(typeof period === "string")) {
    const explicitStartDate = period.startDate >= earliestDate && period.startDate <= latestDate
      ? period.startDate
      : earliestDate

    return ensureValidStartDate(explicitStartDate, earliestDate, latestDate, unmodeledDates)
  }

  const end = moment(endDate)
  let computedStartDate: IsoDate

  switch (period) {
    case MONTH_TO_DATE:
      computedStartDate = momentToIsoDate(end.startOf("month"))
      break
    case YEAR_TO_DATE:
      computedStartDate = momentToIsoDate(end.startOf("year"))
      break
    case ONE_DAY:
    case SAME_DAY:
      computedStartDate = momentToIsoDate(end)
      break
    case ONE_YEAR:
      computedStartDate = momentToIsoDate(end.subtract(1, "year"))
      break
    default: {
      // Handle dynamic periods like "3M", "6M"
      const startDate = periodToDate(endDate, period)
      if (startDate === null) {
        return null
      } else {
        computedStartDate = startDate
      }
    }
  }

  const boundedStartDate = computeStartDate(earliestDate, computedStartDate)

  // Performance requires at least one date prior to the start date for calculations
  const hasPreviousDate = moment(boundedStartDate).diff(moment(earliestDate), "days") >= 1
  if (isPerformance && !hasPreviousDate) {
    return getNextModeledDate(boundedStartDate, latestDate, unmodeledDates)
  }

  return ensureValidStartDate(boundedStartDate, earliestDate, latestDate, unmodeledDates)
}

function getDashboardStartDates(offsetStartDate: Moment, performanceOffsetStartDate: Moment, earliestDate: IsoDate, latestDate: IsoDate, unmodeledDates: IsoDate[] | null, shouldAdjustDates: boolean, endDate: IsoDate, performanceEndDate: IsoDate, isEndDateIntradayToday: boolean) {
  const earliestMoment = moment(earliestDate)
  const clampedStartDate = offsetStartDate.isBefore(earliestMoment)
    ? earliestMoment.clone()
    : offsetStartDate.clone()

  let adjustedStartDate = clampedStartDate
  while (moment(adjustedStartDate).isoWeekday() > 5) {
    adjustedStartDate = adjustedStartDate.add(1, "day")
  }
  let computedStartDate = momentToIsoDate(adjustedStartDate)

  if (isUnmodeledDate(computedStartDate, unmodeledDates)) {
    computedStartDate = getNextModeledDate(computedStartDate, latestDate, unmodeledDates)
  }

  if (computedStartDate > endDate) {
    // An intraday "today" end date is itself selectable even on a weekend
    computedStartDate = isEndDateIntradayToday ? endDate : getClosestSelectableDate(endDate, earliestDate, latestDate, unmodeledDates)
  }

  const performanceClampedStartDate = performanceOffsetStartDate.isBefore(earliestMoment)
    ? earliestMoment.clone()
    : performanceOffsetStartDate.clone()

  let computedPerformanceStartDate = momentToIsoDate(performanceClampedStartDate)
  const hasPreviousDate = performanceClampedStartDate.diff(earliestMoment, "days") >= 1

  if (unmodeledDates && !hasPreviousDate && shouldAdjustDates) {
    computedPerformanceStartDate = getNextModeledDate(performanceClampedStartDate, latestDate, unmodeledDates)
  }

  if (isUnmodeledDate(computedPerformanceStartDate, unmodeledDates)) {
    computedPerformanceStartDate = getNextModeledDate(computedPerformanceStartDate, latestDate, unmodeledDates)
  }

  if (computedPerformanceStartDate > performanceEndDate) {
    computedPerformanceStartDate = isEndDateIntradayToday ? performanceEndDate : getClosestSelectableDate(performanceEndDate, earliestDate, latestDate, unmodeledDates)
  }

  const datesAdjusted = !offsetStartDate.isSame(computedStartDate, "day")

  return { computedStartDate, computedPerformanceStartDate, datesAdjusted }
}

function getClosestSelectableDate(date: IsoDate, earliestDate: IsoDate, latestDate: IsoDate, unmodeledDates: IsoDate[] | null) {
  const rolledBack = isUnmodeledDate(date, unmodeledDates)
    ? getPreviousModeledDate(date, earliestDate, unmodeledDates)
    : date
  // The earliest-date fallback of getPreviousModeledDate can land after the
  // anchor when the whole data window is later — keep the anchor in that case
  if (rolledBack > date) {
    return date
  }
  return rolledBack > latestDate ? latestDate : rolledBack
}

/**
 * Calculates start dates for custom periods with proper handling of intraday mode and data boundaries.
 *
 * This function determines the appropriate start dates for both regular and performance calculations
 * based on user input and various constraints like data availability and intraday mode settings.
 *
 * @param {Object} params - Configuration object for date calculations
 * @param {string} params.startDate - User's chosen start date in YYYY-MM-DD format
 * @param {string} params.endDate - User's chosen end date in YYYY-MM-DD format
 * @param {string} params.initialEndDate - Original end date before any system adjustments
 * @param {string} params.latestDate - Latest date for which data is available
 * @param {boolean} params.isIntradayEnabled - Whether intraday mode is enabled
 * @param {boolean} params.isIntradayBatchAvailable - Whether an intraday batch is available for the instrument
 * @param {boolean} params.isEndDateToday - Whether the end date is today (intraday scenario)
 *
 * @returns {Object} Object containing calculated start dates
 * @returns {moment.Moment} returns.offsetStartDate - Calculated start date for regular calculations
 * @returns {moment.Moment} returns.performanceOffsetStartDate - Calculated start date for performance calculations
 *
 */
export function calculateCustomPeriodStartDates(
  startDate: IsoDate,
  endDate: IsoDate,
  initialEndDate: IsoDate,
  latestDate: IsoDate,
  isIntradayEnabled: boolean,
  isIntradayBatchAvailable: boolean,
  isEndDateToday: boolean,
) {
  const startMoment = moment(startDate)
  const end = moment(endDate)
  const performanceEnd = (isIntradayEnabled && isIntradayBatchAvailable && isEndDateToday)
    ? moment(latestDate)
    : moment(endDate)
  const offset = moment(initialEndDate).diff(startMoment, "days")

  // Check for invalid date range (start date is after end date)
  const hasInvalidDateRange = offset < 0
  if (hasInvalidDateRange) {
    // Use 1-month fallback for invalid ranges
    return {
      offsetStartDate: end.subtract(1, "month"),
      performanceOffsetStartDate: moment(latestDate).subtract(1, "month"),
    }
  }

  // Check if we're in intraday mode with live data
  const isIntradayMode = isIntradayEnabled && isEndDateToday

  // Check if start date is beyond available data (only relevant for non-intraday)
  const startDateBeyondData = !isIntradayMode && startMoment.isAfter(moment(latestDate))

  if (startDateBeyondData) {
    // Adjust dates while maintaining the original time span
    return {
      offsetStartDate: end.subtract(offset, "days"),
      performanceOffsetStartDate: performanceEnd.subtract(offset, "days"),
    }
  }

  // Default: preserve user's explicit start date for valid custom periods
  return {
    offsetStartDate: startMoment.clone(),
    performanceOffsetStartDate: startMoment.clone(),
  }
}

export function getDashboardPeriodStartDates(
  startDate: IsoDate,
  endDate: IsoDate,
  initialEndDate: IsoDate,
  period: Period,
  earliestDate: IsoDate,
  latestDate: IsoDate,
  unmodeledDates: IsoDate[] | null = null,
  shouldAdjustDates: boolean,
  isIntradayEnabled = false,
  isIntradayBatchAvailable = false,
  isEndDateToday = false,
) {
  const end = moment(endDate)
  const isUsingIntraday = isIntradayEnabled && isIntradayBatchAvailable && isEndDateToday
  const performanceEnd = isUsingIntraday ? moment(latestDate) : moment(endDate)
  const performanceEndDate = isUsingIntraday ? latestDate : endDate
  let offsetStartDate: Moment
  let performanceOffsetStartDate

  switch (period) {
    case ONE_DAY: {
      const previousTradingDay = getPreviousModeledDate(momentToIsoDate(end), earliestDate, unmodeledDates)
      offsetStartDate = moment(previousTradingDay).startOf("day")
      performanceOffsetStartDate = moment(getPreviousModeledDate(momentToIsoDate(performanceEnd), earliestDate, unmodeledDates)).startOf("day")
      break
    }
    case MONTH_TO_DATE:
      offsetStartDate = end.startOf("month")
      performanceOffsetStartDate = performanceEnd.startOf("month")
      break
    case ONE_MONTH:
      offsetStartDate = end.subtract(1, "month")
      performanceOffsetStartDate = performanceEnd.subtract(1, "month")
      break
    case THREE_MONTHS:
      offsetStartDate = end.subtract(3, "month")
      performanceOffsetStartDate = performanceEnd.subtract(3, "month")
      break
    case SIX_MONTHS:
      offsetStartDate = end.subtract(6, "month")
      performanceOffsetStartDate = performanceEnd.subtract(6, "month")
      break
    case YEAR_TO_DATE:
      offsetStartDate = end.startOf("year")
      performanceOffsetStartDate = performanceEnd.startOf("year")
      break
    case ONE_YEAR:
      offsetStartDate = end.subtract(1, "year")
      performanceOffsetStartDate = performanceEnd.subtract(1, "year")
      break
    case THREE_YEARS:
      offsetStartDate = end.subtract(3, "year")
      performanceOffsetStartDate = performanceEnd.subtract(3, "year")
      break
    case SAME_DAY:
      offsetStartDate = end.clone()
      performanceOffsetStartDate = performanceEnd.clone()
      break
    default: {
      period satisfies typeof CUSTOM_PERIOD

      const { offsetStartDate: customOffsetStartDate, performanceOffsetStartDate: customPerformanceOffsetStartDate } =
        calculateCustomPeriodStartDates(
          startDate,
          endDate,
          initialEndDate,
          latestDate,
          isIntradayEnabled,
          isIntradayBatchAvailable,
          isEndDateToday,
        )

      offsetStartDate = customOffsetStartDate
      performanceOffsetStartDate = customPerformanceOffsetStartDate
      break
    }
  }

  return getDashboardStartDates(offsetStartDate, performanceOffsetStartDate, earliestDate, latestDate, unmodeledDates, shouldAdjustDates, endDate, performanceEndDate, isIntradayEnabled && isEndDateToday)
}

export function getNextModeledDate(date: IsoDate | Moment, modelLatestDate: IsoDate, unmodeledDates: IsoDate[] | Record<IsoDate, boolean> | null) {
  let formattedDate
  const currentDate = moment(date)
  // We cannot have a start date which is after the latest model date
  if (currentDate.isSameOrAfter(moment(modelLatestDate))) {
    return modelLatestDate
  }

  const threshold = getToday()
  do {
    currentDate.add(1, "day")
    formattedDate = momentToIsoDate(currentDate)
  } while (isUnmodeledDate(formattedDate, unmodeledDates) && threshold > formattedDate)

  return formattedDate
}

export function getPreviousModeledDate(date: IsoDate, modelEarliestDate: IsoDate, unmodeledDates: IsoDate[] | null) {
  const currentDate = moment(date)

  let formattedDate
  do {
    currentDate.subtract(1, "day")
    formattedDate = momentToIsoDate(currentDate)

    if (formattedDate < modelEarliestDate) {
      return modelEarliestDate
    }
  } while (isUnmodeledDate(formattedDate, unmodeledDates))

  return formattedDate
}

export function getDatesAdjusted(period: Period | PeriodObject, startDate: IsoDate, endDate: IsoDate, latestDate: IsoDate) {
  if (!(typeof period === "string")) {
    return startDate !== period.startDate || endDate !== period.endDate
  } else {
    // If a manual period is not selected, enddate should match latestDate
    if (endDate !== latestDate) {
      return true
    }

    // If not, we need to compute the startDate
    const workingStartDate = getStartDate(latestDate, period)
    if (workingStartDate !== startDate) {
      return true
    }
  }

  return false
}

/** Return a time phrase like "later" or "in a day", based on retry-after (number | undefined) */
export function secondsToTimePhrase(retryAfter?: number | null) {
  return retryAfter
    ? `in ${moment.duration(retryAfter, "seconds").humanize()}`
    : "later"
}

/**
 * Returns the given date, clamped between min and max
 */
export function clampDate(date: IsoDate, min?: IsoDate, max?: IsoDate) {
  if (min && date <= min) {
    return min
  }

  if (max && date >= max) {
    return max
  }

  return date
}

// [TODO:TS] Integrate with types
export function isDateValid(date: string) {
  return moment(date, DATE_FORMAT, true).isValid()
}

export function computePeriodOrStartAndEndDates(period: Period | null | undefined | "", startDate?: IsoDate, endDate?: IsoDate) {
  if (startDate && endDate && moment(startDate).isValid() && moment(endDate).isValid()) {
    // If the user has switched the dates by mistake, switch them in the UI as graphQL would do it anyway
    if (startDate > endDate) {
      return { startDate: endDate, endDate: startDate }
    }
    return { startDate, endDate }
  }
  return period || ONE_YEAR
}

export function getRelativeOrCustomPeriod(period: Period | PeriodObject | null) {
  if (!period || isEmpty(period)) {
    return null
  }
  return isString(period) ? period : { startDate: period.startDate, endDate: period.endDate }
}

// The simulation(from, to) API returns a day return for every date in the range, including
// `from` itself (the server compares each date against the previous modeled date on its own).
export function getPerformanceApiStartDate({ period, isIntradayMode, latestDate, performanceStartDate, performanceEndDate }: {
  period: Period
  isIntradayMode: boolean
  latestDate: IsoDate
  performanceStartDate: IsoDate
  performanceEndDate: IsoDate
}): IsoDate {
  if (period === SAME_DAY) {
    return performanceEndDate
  }
  if (period === ONE_DAY) {
    // In intraday mode, 1D means the last complete trading day plus today so far, so the
    // window starts at latestDate. The store's performanceStartDate is one modeled date
    // earlier still (kept for end-vs-start comparison) and would add a third day's return.
    return isIntradayMode && latestDate <= performanceEndDate ? latestDate : performanceEndDate
  }
  return performanceStartDate
}

export function getPeriodLabel(value: Period) {
  if (value === ONE_DAY) {
    return "1 day"
  }
  if (value === SAME_DAY) {
    return "Same Day"
  }
  return chain(PERIOD_OPTIONS)
    .find({ value })
    .get("label")
    .value() || value
}

// UTC ISO comparison handles UTC+10+ users where intraday timestamps (Z suffix) can cross local midnight.
export function isSameTradingDay(date: Moment, dateStr: IsoDate): boolean {
  return toIsoDate(date.toISOString()) === dateStr || date.isSame(dateStr, "day")
}

export function getDriftStartAndEndDates(endDate: IsoDate, earliestDate: IsoDate) {
  const startDate = moment(endDate).subtract(3, "months").format("YYYY-MM-DD")
  let driftStartDate = null

  if (startDate < earliestDate) {
    driftStartDate = earliestDate
  } else {
    driftStartDate = startDate
  }

  return { startDate: driftStartDate, endDate }
}
